import { policy } from '../../data/policies'
import type { Account, AccountOrder, Appointment, PlanItem, SubscriptionStatus } from '../../core/types/account'
import { courseStatus, daysSince, daysUntil, lastVisit, refillState, upcomingAppointments } from '../../utils/account'
import { isAwaitingDelivery, ordersToFollow } from '../../utils/accountOrders'

/**
 * De volgende stap in Mijn omgeving: precies één hoofdactie per klant, per moment
 * (docs/VORMGEVING.md §6). Klanten zijn vaak salon én thuis tegelijk; daarom kiest de regel
 * op wat er nú speelt, niet op een label. De eerste die geldt, wint.
 *
 * Twee lagen, zodat de regel zonder datum of React te testen is:
 * - `journeyFacts(account, deps)` vat het account samen (hier zitten de datums);
 * - `nextStep(facts)` kiest. Puur.
 */

/** Een afspraak binnen zoveel dagen is het belangrijkste wat er is. */
export const APPOINTMENT_SOON_DAYS = 7
/** Een levering binnen zoveel dagen: nog even nakijken of aanpassen. */
export const DELIVERY_SOON_DAYS = 5
const DAYS_PER_WEEK = 7

export type JourneyFacts = {
  /** Een pakket dat nog komt (niet bezorgd, niet geannuleerd, geen cadeaubon alleen). */
  awaitingOrder: { id: string; status: AccountOrder['status'] } | null
  nextAppointment: { appointment: Appointment; inDays: number } | null
  /** Alleen als abonnementen aan staan en er een niet-gestopt abonnement is. */
  subscription: { status: Exclude<SubscriptionStatus, 'gestopt'>; nextDelivery: string; inDays: number; pausedUntil?: string } | null
  /** Het product dat het eerst op raakt en niet al vanzelf komt (abonnement). Leverbaar. */
  runningLow: { productId: string; runsOutOn: string; inDays: number } | null
  course: { treatmentSlug: string; done: number; total: number; nextToBook: number | null; booked: boolean; finished: boolean } | null
  lastVisit: { treatmentSlug: string; daysAgo: number } | null
  /** Nog niets: geen bestelling, afspraak, bezoek of huidplan. */
  isNew: boolean
}

export type NextStep =
  | { kind: 'pakket'; orderId: string }
  | { kind: 'afspraak'; appointmentId: string }
  | { kind: 'levering' }
  | { kind: 'bijvullen'; productId: string }
  | { kind: 'kuur'; session: number }
  | { kind: 'hervatten' }
  | { kind: 'terugkomen'; treatmentSlug: string; weeksAway: number }
  | { kind: 'nieuw' }
  | { kind: 'rust' }

export type NextStepKind = NextStep['kind']

/** De volgorde uit het plan. Puur: alleen de feiten bepalen de uitkomst. */
export function nextStep(f: JourneyFacts, revisitAfterWeeks: number = policy.revisitAfterWeeks): NextStep {
  if (f.awaitingOrder) return { kind: 'pakket', orderId: f.awaitingOrder.id }
  if (f.nextAppointment && f.nextAppointment.inDays <= APPOINTMENT_SOON_DAYS) return { kind: 'afspraak', appointmentId: f.nextAppointment.appointment.id }
  if (f.subscription?.status === 'actief' && f.subscription.inDays <= DELIVERY_SOON_DAYS) return { kind: 'levering' }
  if (f.runningLow) return { kind: 'bijvullen', productId: f.runningLow.productId }
  if (f.course && !f.course.finished && f.course.nextToBook && !f.course.booked) return { kind: 'kuur', session: f.course.nextToBook }
  if (f.subscription?.status === 'gepauzeerd') return { kind: 'hervatten' }
  if (f.lastVisit && !f.nextAppointment && f.lastVisit.daysAgo >= revisitAfterWeeks * DAYS_PER_WEEK)
    return { kind: 'terugkomen', treatmentSlug: f.lastVisit.treatmentSlug, weeksAway: Math.round(f.lastVisit.daysAgo / DAYS_PER_WEEK) }
  if (f.isNew) return { kind: 'nieuw' }
  return { kind: 'rust' }
}

type Deps = {
  /** Het huidplan zoals het getoond wordt (Sarahs plan of de voorlopige routine): useSkinPlan. */
  planItems: PlanItem[]
  isGiftCard: (productId: string) => boolean
  /** Bestaat het product en is het leverbaar? */
  canBuy: (productId: string) => boolean
  subscriptionsOn: boolean
}

/** Het account samengevat in feiten voor `nextStep`. Hier zitten de datums (vandaag). */
export function journeyFacts(account: Account, deps: Deps): JourneyFacts {
  const following = ordersToFollow(account.orders, deps.isGiftCard).find((o) => isAwaitingDelivery(o, deps.isGiftCard))
  const upcoming = upcomingAppointments(account.appointments)
  const next = upcoming[0]
  const sub = account.subscription
  const subOn = deps.subscriptionsOn && sub && sub.status !== 'gestopt' ? sub : null
  const inSubscription = new Set(subOn?.status === 'actief' ? subOn.items.map((i) => i.productId) : [])

  const low = deps.planItems
    .filter((i) => !inSubscription.has(i.productId) && deps.canBuy(i.productId))
    .map((i) => ({ item: i, refill: refillState(i) }))
    .filter((x) => x.refill.level !== 'ruim')
    .sort((a, b) => a.refill.runsOutOn.localeCompare(b.refill.runsOutOn))[0]

  const cs = courseStatus(account)
  const visit = lastVisit(account)

  return {
    awaitingOrder: following ? { id: following.id, status: following.status } : null,
    nextAppointment: next ? { appointment: next, inDays: daysUntil(next.date) } : null,
    subscription: subOn
      ? { status: subOn.status as 'actief' | 'gepauzeerd', nextDelivery: subOn.nextDelivery, inDays: daysUntil(subOn.nextDelivery), pausedUntil: subOn.pausedUntil }
      : null,
    runningLow: low ? { productId: low.item.productId, runsOutOn: low.refill.runsOutOn, inDays: daysUntil(low.refill.runsOutOn) } : null,
    course: cs
      ? {
          treatmentSlug: cs.course.treatmentSlug,
          done: cs.course.done,
          total: cs.course.total,
          nextToBook: cs.nextToBook ?? null,
          booked: upcoming.some((a) => a.treatmentSlug === cs.course.treatmentSlug),
          finished: cs.finished,
        }
      : null,
    lastVisit: visit ? { treatmentSlug: visit.treatmentSlug, daysAgo: daysSince(visit.date) } : null,
    isNew: account.orders.length === 0 && account.visits.length === 0 && account.appointments.length === 0 && account.plan.items.length === 0,
  }
}
